// noinspection JSUnresolvedVariable
import {toUrl} from "../../helpers/url";
import createElement from "../../helpers/createElement";
import {Calendar} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import {initCalendarCreateEventModal, showCalendarCreateEventModal} from "./calendarCreateEventModal";
import {deleteConfirmation, handleAjaxSuccess} from "../../helpers/SwalHelper";
import Axios from "axios";
import handleAjaxError from "../../helpers/handleAjaxError";
import {displayManageWeatherModal, setManageWeatherCallback} from "./manageWeatherModal";
import {waitUntil} from "../../helpers/waitUntil";
import {setupFileUploader} from "../../common/filesUploader";
import {nl2br} from "../../helpers/nl2br";
import {randomString} from "../../helpers/random";
import {allowInlineUpdate, initStatusSelect} from "../../common/todolists/itemStatusSelect";
import {updateProgressBar} from "../../helpers/progressBar/progressBar";

let calendarContainer = null;
let calendarInstance = null;
let eventModal = null;
let eventFilterSelect = null;
let allEvents = null;
let eventsUniqueIds = {};
let toggleViewButton = null;
let weekViewContentHeight = 200;

// Calendar options
let showProjectOnEvents = false;

let isAdminView = true;

let initialView = 'dayGridMonth';

let showToggleView = false;

let onViewChange = null;

/**
 * Initializes a calendar on the document, only one calendar can exist per page, needs
 * a rewrite to clean up and make it self contained
 *
 * @param {Object.<string,any>} calendarOptions
 * @param {Object.<string,any>} modalOptions
 * @param {Boolean} forAdminView
 */
function initCalendar(calendarOptions = {}, modalOptions = {}, forAdminView = true) {
	if (calendarOptions.showProjectOnEvents) {
		showProjectOnEvents = calendarOptions.showProjectOnEvents;
	}
	if (calendarOptions.initialView) {
		initialView = calendarOptions.initialView;
	}
	if (calendarOptions.showToggleView) {
		showToggleView = calendarOptions.showToggleView;
	}
	if (calendarOptions.onViewChange) {
		onViewChange = calendarOptions.onViewChange;
	}

	isAdminView = forAdminView;

	allEvents = window.events;

	const cardContainer = document.querySelector('.Calendar');

	// Setup calendar views
	calendarContainer = cardContainer.querySelector('.calendar-container');

	let fullCalendarOptions = {};
	if (calendarOptions.fullCalendarOptions) {
		fullCalendarOptions = calendarOptions.fullCalendarOptions;
	}

	calendarInstance = new Calendar(calendarContainer, {
		plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
		initialView: initialView,
		headerToolbar: {
			left: '',
			center: 'prev title next',
			right: '',
		},
		dayMaxEventRows: initialView === 'dayGridMonth' ? 3 : 5,
		events: getEventsForFullCalendar,
		initialDate: calendarContainer.dataset.month,
		eventClick: function (info) {
			displayEvent(info);
		},
		eventContent: (arg) => {
			return {domNodes: [getEventTitle(arg.event)]}
		},
		eventDidMount: (arg) => {
			const eventId = getUniqueId();
			arg.event.setExtendedProp('_domId', eventId);
			arg.el.dataset.eventId = eventId;
		},
		moreLinkClick: () => {
			// We need to properly position the popover after its being
			// rendered, so its not cutoff by the card borders
			waitUntil(() => {
				const popover = document.querySelector('.fc-popover');

				return popover && popover.style.top && popover.style.left;
			}).then(() => {
				const popover = document.querySelector('.fc-popover');

				const top = parseFloat(popover.style.top);
				const left = parseFloat(popover.style.left);

				const height = popover.getBoundingClientRect().height;
				const width = popover.getBoundingClientRect().width;

				const calendarHarness = document.querySelector('.fc-view-harness');

				const calendarHarnessHeight = calendarHarness.getBoundingClientRect().height;
				const calendarHarnessWidth = calendarHarness.getBoundingClientRect().width;

				// Overflowing horizontally
				if (left + width >= calendarHarnessWidth) {
					popover.style.left = `${calendarHarnessWidth - width - 2}px`;
				}

				// Overflowing vertically
				if (top + height >= calendarHarnessHeight) {
					popover.style.top = `${calendarHarnessHeight - height - 2}px`;
				}
			})

			return 'popover';
		},
		dayCellDidMount: renderWeatherForecast,
		...fullCalendarOptions,
	})

	calendarInstance.render();

	// Update Calendar Header
	const header = document.querySelector('.Calendar .fc-header-toolbar');

	// Add card header class to calendar header
	header.classList.add('card-header');

	eventModal = document.querySelector('.calendar-event-modal');

	// Add card header using the first calendar header fc-toolbar-chunk
	const headerChunks = header.querySelectorAll('.fc-toolbar-chunk');

	headerChunks[0].appendChild(createElement(`<h4>Calendar</h4>`));

	// Change button styling
	header.querySelectorAll('.fc-button').forEach(fcButton => {
		// Remove default classes
		fcButton.classList.remove('fc-button', 'fc-button-primary');
		// Add bootstrap classes
		fcButton.classList.add('btn', 'btn-primary');
	})

	if (showToggleView) {
		// Add collapse button on the last fc-toolbar-chunk
		toggleViewButton = createElement(
			`
			<button
				class="btn btn-light btn-sm ml-auto px-2 toggle-view-button"
				title="${initialView === 'dayGridMonth' ? 'Change to week view' : 'Change to month view'}"
			>
				<span class="mdi ${initialView === 'dayGridMonth' ? 'mdi-calendar-range' : 'mdi-calendar-month'}"></span>
			</button>
		`);

		toggleViewButton.addEventListener('click', toggleView);

		headerChunks[2].appendChild(toggleViewButton);
	}

	setupEventSelect();

	if (forAdminView) {
		// Setup event creation modal
		if (document.querySelector('.calendar-create-event-modal')) {
			initCalendarCreateEventModal(null, modalOptions);

			// Setup event creation modal caller
			document.querySelectorAll('.create-calendar-event').forEach(
				button => button.onclick = () => showCalendarCreateEventModal()
			);
		}

		// Set up manage weather modal callback
		setManageWeatherCallback(() => {
			loading();
			calendarInstance.destroy();
			calendarInstance.render();
			stopLoading();
		})
	}

	// Check if an event should be highlighted
	const eventId = new URLSearchParams(window.location.search).get('event_id');
	if (eventId) {
		// highlight custom event

		// noinspection EqualityComparisonWithCoercionJS
		const event = calendarInstance.getEvents()
			.filter(event => event.extendedProps.event_id == eventId)[0];

		if (event) {
			const eventStartDate = event._instance.range.start;

			calendarInstance.gotoDate(eventStartDate);

			// We wait until full calendar has rendered the event and we have
			// assigned its dom id to it so we can retrieve the dom element
			waitUntil(() => {
				// We need to retrieve the event from the calendar api again
				// otherwise it won't contain the dom id

				// noinspection EqualityComparisonWithCoercionJS
				const event = calendarInstance.getEvents()
					.filter(event => event.extendedProps.event_id == eventId)[0];

				return event.extendedProps._domId !== undefined
			}).then(() => {
				// We need to retrieve the event from the calendar api again
				// otherwise it won't contain the dom id

				// noinspection EqualityComparisonWithCoercionJS
				const event = calendarInstance.getEvents()
					.filter(event => event.extendedProps.event_id == eventId)[0];

				const domElement = document.querySelector(`.fc-daygrid-event[data-event-id="${event.extendedProps._domId}"]`);

				domElement.classList.add('highlighted');

				window.scrollToElementNative(domElement);
			})
		}
	}

	// Setup legends events toggle
	cardContainer.querySelectorAll('.legend-tag').forEach(legendTag => {
		legendTag.title = 'Toggle Event Type';
		legendTag.addEventListener('click', () => {
			legendTag.classList.toggle('hidden');
			calendarInstance.refetchEvents();
			updateEventFilterEntries();
		})
	})

	// Calendar ready to be displayed, remove classes hiding it
	cardContainer.classList.remove('opacity-0', 'pointer-events-none');

	// Methods

	/**
	 * Setups event filter using select2
	 */
	function setupEventSelect() {
		// Setup event filter select
		const eventsForSelect2 = getEvents().map((event, index) => {
			event.id = index;
			return event;
		})

		eventFilterSelect = document.querySelector('.Calendar .event-filter');
		// noinspection JSUnresolvedFunction,JSUnusedGlobalSymbols
		$(eventFilterSelect).select2({
			theme: 'bootstrap4',
			placeholder: 'Search Events',
			allowClear: true,
			data: eventsForSelect2,
			templateResult: event => {
				if (!event.id) {
					return null;
				}
				console.log('templating', event);
				const type = event.type.replace(/(^\w|\s\w)/g, letter => letter.toUpperCase());

				let title = `${type} - ${event.title}`;

				if (showProjectOnEvents) {
					// noinspection JSUnresolvedVariable
					title = `${type} - ${event.project_name} - ${event.title}`;
				}

				const element = createElement(`
				<div class="d-flex">
					<div>
						${title}
					</div>
					<div class="ml-auto">
						${event.start}
					</div>
				</div>
			`);

				return $(element);
			},
			matcher: (params, data) => {
				// noinspection JSDeprecatedSymbols
				let search = $.trim(params.term);
				if (!search) {
					return data;
				}

				search = search.toLowerCase();

				// noinspection JSUnresolvedVariable
				const searchTargets = [
					data.type.toLowerCase(),
					data.title.toLowerCase(),
					data.project_name.toLowerCase(),
				];

				for (const target of searchTargets) {
					if (target.indexOf(search) !== -1)
						return data;
				}

				return null;
			}
		});

		// Clear selection
		$(eventFilterSelect).val(null).trigger('change');

		// Set listener to focus date
		$(eventFilterSelect).on('select2:select', e => {
			const event = e.params.data;
			if (event) {
				// noinspection JSUnresolvedVariable
				calendarInstance.gotoDate(event.start);
			}
			// Clear selection
			$(eventFilterSelect).val(null).trigger('change');

			/** Scroll to selected event */

			let cell = null;

			document.querySelectorAll('.fc-event-main').forEach(eventCell => {
				eventCell.querySelectorAll('div').forEach(div => {
					if (div.innerText === event.title) {
						cell = div;
					}
				})
			});

			if (cell) {
				window.scrollToElementNative(cell);
			}
		})
	}

	/**
	 * Retrieves events that should be displayed on the calendar
	 */
	function getEvents() {
		const filteredEventTypes = Array
			// Get all hidden event types from the legend
			.from(cardContainer.querySelectorAll('.legend-tag.hidden'))
			// Retrieve the event type
			.map(tag => tag.dataset.eventType);


		// Clone events in case someone downstream modifies the original events
		const clonedEvents = JSON.parse(JSON.stringify(allEvents));

		return clonedEvents.filter(event => {
			// Return all events that are not filtered
			return filteredEventTypes.includes(event.type) === false;
		})
	}

	/**
	 * Full calendar handler to retrieve more events when required
	 *
	 * @param info
	 * @param successCallback
	 */
	function getEventsForFullCalendar(info, successCallback) {
		// We don't care about the info, because we are returning the events from memory
		// We do it this way so we can apply local filters, like the legends toggle
		// We always return all available events, not efficient but it works
		successCallback(getEvents());
	}

	/**
	 * Updates the event filter entries to only display visible events
	 */
	function updateEventFilterEntries() {
		// Destroy existing select2 instance
		$(eventFilterSelect).select2('destroy');

		// Clean up existing select options
		eventFilterSelect.querySelectorAll('option')
			.forEach(option => option.parentElement.removeChild(option));

		// Recreate select2 instance
		setupEventSelect();
	}
}

function setUpModalButtons(event) {
	const type = event.extendedProps.type;

	const deleteButton = eventModal.querySelector('.delete-button');
	const editButton = eventModal.querySelector('.edit-button');

	if (type === 'custom' || type === 'contractor') {
		deleteButton.classList.remove('d-none');
		editButton.classList.remove('d-none');

		deleteButton.onclick = () => handleEventDelete(event);
		editButton.onclick = () => handleEventEdit(event);
	} else {
		deleteButton.classList.add('d-none');
		editButton.classList.add('d-none');
	}

	// Also remove the buttons if the user cant use them
	// noinspection JSUnresolvedVariable
	if (!window.userCanUpdateEvent) {
		editButton.classList.add('d-none');
	}
	// noinspection JSUnresolvedVariable
	if (!window.userCanDeleteEvent) {
		deleteButton.classList.add('d-none');
	}
}

function displayEvent(info) {
	const {event} = info;

	// Hide events popover if visible
	const popover = document.querySelector('.fc-popover');
	if (popover) {
		// A popover can be displayed when the number of events exceeds the max
		// allowed in a single day, which displays all events, however it needs
		// to be manually closed after a modal is selected
		popover.querySelector('.fc-popover-close ').click();
	}

	eventModal.querySelector('.modal-title').innerText = event.extendedProps.typeName ?
		event.extendedProps.typeName : event.extendedProps.type;

	eventModal.querySelector('.modal-body').innerHTML = '';
	eventModal.querySelector('.modal-body').appendChild(renderEvent(event));

	setUpModalButtons(event);

	$(eventModal).modal('show');
}

/**
 * Renders event modal inner content
 *
 * @param event
 * @return {Element|Text|Comment|Document|DocumentFragment|(Element | Text | Comment | Document | DocumentFragment)[]|*}
 */
function renderEvent(event) {
	const extendedProps = event.extendedProps;

	let projectName = '';

	if (showProjectOnEvents) {
		projectName = `
			<div class="d-flex flex-row small text-muted">
				<label class="mr-2">Project</label>
				<div>
					<a href="${toUrl(`/admin/projects/dashboard/${event.extendedProps.project_id}`)}" target="_blank">${event.extendedProps.project_name}</a>
				</div>
			</div>
		`;
	}

	switch (extendedProps.type) {
		case 'vendor payment':
			// noinspection JSUnresolvedVariable
			const relatedInvoice = extendedProps.invoice ? `
				<a href="${extendedProps.invoice.url}" target="_blank" title="Edit Invoice">
					${extendedProps.invoice.description ? extendedProps.invoice.description : 'No Invoice Description'} - ${extendedProps.invoice.amount} Due ${extendedProps.invoice.due_date}
				</a>
			` : 'No Related Invoice';

			let receiptHeader = `<h4>Receipt #${extendedProps.payment_id}</h4>`;

			if (extendedProps.paymentUrl) {
				receiptHeader = `
					<a href="${extendedProps.paymentUrl}" target="_blank" title="Edit Payment">
						<h4>Payment #${extendedProps.payment_id}</h4>
					</a>
				`;
			}

			// noinspection JSUnresolvedVariable
			const paymentElement = createElement(`
				<div>
					${receiptHeader}
					${projectName}
					<h5>${extendedProps.category_names}</h5>

					<div class="d-flex align-items-center">
						<div>Paid on ${extendedProps.created_at}</div>
						<div class="ml-auto">${extendedProps.amount}</div>
					</div>
					<div class="d-flex flex-column mt-1">
						<label>Description</label>
						<div class="ml-2">
							${extendedProps.description ? extendedProps.description : 'No description'}
						</div>
					</div>
					<hr>
					<h4>Payment Files</h4>
					${extendedProps.paymentFiles.length ? `
							<div class="payment-files-container no-uploads"></div>
							` : `<p class="text-muted">No Files Uploaded</p>`}
					${isAdminView ? `
						<hr>
						<h4>Related Invoice</h4>
						${relatedInvoice}
					` : ''}

				</div>
			`);

			// Initialize file gallery
			if (extendedProps.paymentFiles.length) {
				setupFileUploader(
					{
						target: paymentElement.querySelector('.payment-files-container'),
						height: 240,
					},
					null,
					extendedProps.paymentFiles,
				);
			}

			return paymentElement;
		case 'incoming invoice':
			let invoiceHeader = `
				<h4>
					Invoice #${extendedProps.invoice_id}
				</h4>
			`
			if (extendedProps.invoiceRoute) {
				invoiceHeader = `
					<h4>
						<a href="${extendedProps.invoiceRoute}" target="_blank">
							Invoice #${extendedProps.invoice_id}
						</a>
					</h4>
				`
			}

			const invoiceElement = createElement(`
				<div>
					${invoiceHeader}
					${projectName}

					<h5>${extendedProps.category_name ? extendedProps.category_name : 'No Category'}</h5>

					<div class="d-flex align-items-center">
						<div>${extendedProps.due_date}</div>
						<div class="ml-auto">${extendedProps.amount}</div>
					</div>
					<div class="d-flex flex-column mt-1">
						<label>Description</label>
						<div class="ml-2">
							${extendedProps.description ? extendedProps.description : 'No Description'}
						</div>
					</div>
					<hr>
					<h4>Invoice</h4>
					${extendedProps.invoiceFiles.length ? `
							<div class="invoice-files-container no-uploads"></div>
							` : `<p class="text-muted">No File Uploaded</p>`}
				</div>
			`);

			// Initialize file gallery
			if (extendedProps.invoiceFiles.length) {
				setupFileUploader(
					{
						target: invoiceElement.querySelector('.invoice-files-container'),
						height: 240,
					},
					null,
					extendedProps.invoiceFiles,
				);
			}

			return invoiceElement;
		case 'custom':

			let customEventDates = `<h5>${extendedProps.start_formatted}</h5>`;
			if (extendedProps.start_formatted !== extendedProps.end_formatted) {
				// Start and end dates are different
				customEventDates = `<h5>${extendedProps.start_formatted} - ${extendedProps.end_formatted}</h5>`;
			}

			return createElement(`
				<div>
					<h4>
						${event.title}
					</h4>
					${projectName}
					${customEventDates}
					<label>Description</label>
					<p class="${!extendedProps.description ? 'text-muted' : ''}">
						${extendedProps.description ? extendedProps.description : 'No Description'}
					</p>
				</div>
			`);
		case 'contractor':
			let contractorEventDates = `<h5>${extendedProps.start_formatted}</h5>`;
			if (extendedProps.start_formatted !== extendedProps.end_formatted) {
				// Start and end dates are different
				contractorEventDates = `<h5>${extendedProps.start_formatted} - ${extendedProps.end_formatted}</h5>`;
			}

			let contractorData = '<label>Contractor</label>';
			if (extendedProps.contractor) {
				contractorData += `<div class="pl-3">${extendedProps.contractorName}</div>`
			} else {
				contractorData += `<div class="pl-3 text-muted">None</div>`
			}

			return createElement(`
				<div>
					<h4>
						${event.title}
					</h4>
					${projectName}
					${contractorEventDates}
					${contractorData}
					<label>Description</label>
					<p class="pl-3 ${!extendedProps.description ? 'text-muted' : ''}">
						${extendedProps.description ? extendedProps.description : 'No Description'}
					</p>
				</div>
			`);
		case 'site visit':
			/** Setup site visit own files */
			let siteVisitFilesDomElement = `<p class="text-muted">No Files Uploaded</p>`;
			let siteVisitFilesGalleryId = `site-visit-files-gallery-${event.id}`
			const files = extendedProps.files;
			if (files && files.length) {
				siteVisitFilesDomElement = `<div id="${siteVisitFilesGalleryId}" class="uppy-dashboard no-uploads"></div>`;
			}

			/** Setup description */
			let description = '';

			if (isAdminView) {
				if (extendedProps.notes) {
					description = extendedProps.notes;
				} else if (extendedProps.admin_notes) {
					description = extendedProps.admin_notes;
				}
			} else {
				description = extendedProps.notes ? extendedProps.notes : '';
			}

			/** Setup issues */
			let issuesDomElement = '<p class="text-muted">No Issues Found</p>';
			if (extendedProps.issues && extendedProps.issues.length) {
				issuesDomElement = '';
				extendedProps.issues.forEach((issue, index) => {
					if (index > 0) {
						issuesDomElement += '<hr>';
					}
					let issueFiles = `<p class="text-muted">No Files Uploaded</p>`;
					if (issue.files.length) {
						issueFiles = `<div id="site-visit-issues-files-gallery-${index}" class="uppy-dashboard no-uploads"></div>`;
					}

					issuesDomElement += `
						<div class="issue pl-4">
							<h6 class="mb-0">${issue.title}</h6>
							<p class="text-muted my-0">${issue.category}</p>
							<p>${issue.description}</p>
							<label>Files</label>
							${issueFiles}
						</div>
					`;
				})
			}

			let todoItemsDomElement = '<p class="text-muted">No Items Added</p>';
			if (extendedProps.todoItems && extendedProps.todoItems.length) {
				todoItemsDomElement = '';
				extendedProps.todoItems.forEach(item => {
					todoItemsDomElement += item;
				})
			}

			const eventElement = createElement(`
				<div>
					<h4>
						<a href="${extendedProps.route}" target="_blank">
							${event.title}
						</a>
					</h4>
					${projectName}
					<div class="d-flex flex-column mt-1">
						<label>Site visit on ${extendedProps.visitFormattedDate}</label>
						<p class="${!description ? 'text-muted' : ''}">
							${description ? description : 'No Description'}
						</p>
					</div>
					<hr>
					<h5>Files</h5>
					${siteVisitFilesDomElement}
					<hr>
					<h5>Issues</h5>
					${issuesDomElement}
					<div class="${isAdminView ? '' : 'd-none'}">
						<hr>
						<h5>Todo Items</h5>
						${todoItemsDomElement}
					</div>
				</div>
			`);

			waitUntil(() => {
				return eventElement.parentElement !== null;
			}, {timeoutAfter: 5000})
				.then(() => {
					// Initialize file gallery for files belonging to the site visit itself
					if (document.querySelector(`#${siteVisitFilesGalleryId}`)) {
						setupFileUploader({
							target: `#${siteVisitFilesGalleryId}`,
							height: 240,
						}, null, files)
					}
					// Initialize issues file galleries
					if (extendedProps.issues && extendedProps.issues.length) {
						extendedProps.issues.forEach((issue, index) => {
							if (issue.files && issue.files.length) {
								setupFileUploader({
									target: `#site-visit-issues-files-gallery-${index}`,
									height: 240,
								}, null, issue.files)
							}
						})
					}

					// Initialize status select
					if (isAdminView) {
						eventModal.querySelectorAll('.status-select').forEach(select => {
							initStatusSelect(select);
							allowInlineUpdate(select, data => {
								// We need to update the original itemHtml so when the modal opens
								// again it contains the updated status

								// Get select position in parent
								const selectIndex = Array.from(select.parentNode.children).indexOf(select);

								// Update html on parent
								// noinspection JSUnresolvedVariable
								extendedProps.todoItems[selectIndex] = data.itemHtml;
							}, {
								// Request item html back
								withItemHtml: true,
								itemHtmlOptions: {
									'new': false,
									'static_status': false,
									'withDeleteButton': false,
									'withEditButton': false,
								}
							});
						});
					}
				})

			return eventElement;
		case 'todolist item':
			const timeframe = `
				<div class="row">
					<div class="col">
						<label>Completed Before</label>
						<p>${extendedProps.end_date_display}</p>
					</div>
					<div class="col">
						<label>Completed After</label>
						<p>${extendedProps.start_date_display}</p>
					</div>
				</div>
			`;

			const taskDescription = extendedProps.description ?
				nl2br(extendedProps.description) : '<span class="text-muted">No description</span>'

			return createElement(`
				<div>
					<div class="d-flex align-items-center">
						<h4>Task: ${event.title}</h4>
						<a href="${extendedProps.view_route}" target="_blank" class="btn btn-primary ml-auto">View</a>
					</div>
					${projectName}
					<div>
						<small class="text-muted">
							By ${extendedProps.created_by ? extendedProps.created_by : 'Unknown'}
						</small>
					</div>
					<p class="my-2 px-2">
						${taskDescription}
					</p>
					${timeframe}
				</div>
			`);
		case 'timeline milestone':
			// Create and update progress bar
			const progressBar = getNewProgressBar();

			updateProgressBar(progressBar, extendedProps.progress);

			// Turn back into HTML
			const progressBarHtml = progressBar.outerHTML;

			return createElement(`
				<div>
					<div class="d-flex align-items-center">
						<h4>Milestone: ${event.title}</h4>
						<a href="${extendedProps.route}" target="_blank" class="btn btn-primary ml-auto">View In Timeline</a>
					</div>
					${projectName}
					<p class="my-2 px-2">
						<div class="row">
							<div class="col">
								<label>Progress</label>
								<div class="py-1">
									${progressBarHtml}
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col">
								<label>Start Date</label>
								<p>${extendedProps.start_formatted}</p>
							</div>
							<div class="col">
								<label>Projected End Date</label>
								<p>${extendedProps.end_formatted}</p>
							</div>
						</div>
					</p>
				</div>
			`);
	}

	return createElement('UNSUPPORTED EVENT TYPE');
}

/**
 * The event HTML as displayed on the calendar day container
 *
 * @param event
 * @return {Element|Text|Comment|Document|DocumentFragment|JQuery.Node[]|*}
 */
function getEventTitle(event) {
	let title = event.title;

	const extendedProps = event.extendedProps;

	let projectName = '';

	switch (extendedProps.type) {
		case 'vendor payment':
			if (showProjectOnEvents) {
				projectName = extendedProps.project_name;
			}

			return createElement(`
				<div class="d-flex flex-column align-items-start">
					<div>
						${title}
					</div>
					<div class="w-100 d-flex flex-row">
						<div class="opacity-75 constrained-project-name">
							${projectName}
						</div>
						<div class="ml-auto">
							${extendedProps.amount}
						</div>
					</div>
				</div>
			`)
		case 'incoming invoice':
			if (showProjectOnEvents) {
				projectName = extendedProps.project_name;
			}

			return createElement(`
				<div class="d-flex flex-column align-items-start">
					<div>
						${title}
					</div>
					<div class="w-100 d-flex flex-row">
						<div class="opacity-75 constrained-project-name">
							${projectName}
						</div>
						<div class="ml-auto">
							${extendedProps.amount}
						</div>
					</div>
				</div>
			`)
		case 'custom':
			if (showProjectOnEvents) {
				projectName = `
					<div class="align-self-start opacity-75">
						On '${extendedProps.project_name}' Project
					</div>
				`;
			}

			return createElement(`
				<div class="d-flex flex-column align-items-start">
					<div>
						${title}
					</div>
					${projectName}
				</div>
			`)
		case 'contractor':
			if (showProjectOnEvents) {
				projectName = `
					<div class="align-self-start opacity-75">
						On '${extendedProps.project_name}' Project
					</div>
				`;
			}

			return createElement(`
				<div class="d-flex flex-column align-items-start">
					<div>
						${title}
					</div>
					${projectName}
				</div>
			`)
		case 'site visit':
			if (showProjectOnEvents) {
				projectName = `
					<div class="align-self-start opacity-75">
						On '${extendedProps.project_name}' Project
					</div>
				`;
			}

			return createElement(`
				<div class="d-flex flex-column align-items-start">
					<div>
						${title}
					</div>
					${projectName}
				</div>
			`)
		case 'todolist item':
			if (showProjectOnEvents) {
				projectName = `
					<div class="align-self-start opacity-75">
						On '${extendedProps.project_name}' Project
					</div>
				`;
			}

			return createElement(`
				<div class="d-flex flex-column align-items-start">
					<div>
						Task: ${title}
					</div>
					${projectName}
				</div>
			`)
		case 'timeline milestone':
			if (showProjectOnEvents) {
				projectName = `
					<div class="align-self-start opacity-75">
						On '${extendedProps.project_name}' Project
					</div>
				`;
			}

			return createElement(`
				<div class="d-flex flex-column align-items-start">
					<div>
						Milestone ${extendedProps.startOrEnd}
					</div>
					<div>
						${title}
					</div>
					${projectName}
				</div>
			`)
	}

	return createElement('<div>Unsupported Event</div>')
}


/** Event Handling */

function handleEventDelete(event) {
	const type = event.extendedProps.type;

	switch (type) {
		case 'custom':
			handleCustomEventDelete(event);
			break;
		case 'contractor':
			handleContractorEventDelete(event);
			break;
	}
}

function handleCustomEventDelete(event) {
	deleteConfirmation(
		'Are you sure you want to delete this custom event?',
	).then(result => {
		if (result.value) {
			loading();

			Axios.post(toUrl(`/admin/calendar/events/${event.extendedProps.event_id}`))
				.then(handleAjaxSuccess)
				.catch(handleAjaxError)
				.finally(stopLoading);
		}
	})
}

function handleContractorEventDelete(event) {
	deleteConfirmation(
		'Are you sure you want to delete this contractor schedule event?',
	).then(result => {
		if (result.value) {
			loading();

			Axios.post(toUrl(`/admin/calendar/events/${event.extendedProps.event_id}`))
				.then(handleAjaxSuccess)
				.catch(handleAjaxError)
				.finally(stopLoading);
		}
	})
}

function handleEventEdit(event) {
	$(eventModal).modal('hide');

	showCalendarCreateEventModal(event);
}

/** Weather stuff */

function renderWeatherForecast(arg) {
	// noinspection JSUnresolvedVariable
	if (!window.forecasts) {
		return;
	}
	const cell = arg.el;

	const date = cell.dataset.date;

	const cellHeader = cell.querySelector('.fc-daygrid-day-top');

	if (!cellHeader) {
		return;
	}

	const forecast = window.forecasts[date];

	let weatherElement;

	if (forecast) {
		let opacity = getOpacityFromForecast(forecast);
		let warningIcon = getWarningIconFromForecast(forecast);
		let color = getColorFromForecast(forecast);

		weatherElement = createElement(`
			<div
				class="weather-event"
				title="${forecast.title}"
			>
				<div class="icons">
					${warningIcon}
					<i
						class="mdi ${forecast.icon}"
						style="opacity: ${opacity}; color: ${color}"
					></i>
				</div>
			</div>
		`);

	} else {
		weatherElement = createElement(`
			<div
				class="weather-event"
				title="clear day"
			>
				<div class="icons">
					<i
						class="mdi mdi-weather-sunny"
						style="opacity: 1; color: #ffc107"
					></i>
				</div>
			</div>
		`);

	}

	// noinspection JSUnresolvedVariable
	if (window.userCanManageWeather) {
		// Add editable class
		weatherElement.classList.add('editable');

		// Add pencil icon
		weatherElement.appendChild(createElement(`
			<div class="edit-icon-container">
				<i
					class="mdi mdi-pencil"
				></i>
			</div>
		`));

		// Add handler
		weatherElement.onclick = () => displayManageWeatherModal(forecast, date, window.project.id);
	}


	cellHeader.appendChild(weatherElement);
}

function getOpacityFromForecast(forecast) {

	if (forecast.type === 'clear') {
		// Clear days always display fully
		return 1;
	}

	const intensitiesMap = {
		10: 0.3,
		20: 0.8,
		30: 1,
	}

	// noinspection JSUnresolvedVariable
	return intensitiesMap[forecast.intensity];
}

function getWarningIconFromForecast(forecast) {
	// noinspection JSUnresolvedVariable
	if (forecast.causes_disruption) {
		return `
			<i
				class="mdi mdi-alert-circle text-danger"
			></i>
		`
	}

	// noinspection JSUnresolvedVariable
	if (forecast.ignored) {
		return `
			<i
				class="mdi mdi-alert-circle text-muted"
			></i>
		`
	}

	return '';
}

function getColorFromForecast(forecast) {
	const intensitiesMap = {
		drizzle: '#8ec9ff',
		rain: '#007bff',
		snow: '#858585',
		clear: '#ffc107'
	}

	// noinspection JSUnresolvedVariable
	return intensitiesMap[forecast.type];
}

function getUniqueId() {
	let id;
	do {
		id = randomString(16);
	} while (eventsUniqueIds[id] !== undefined)

	// Mark as used
	eventsUniqueIds[id] = true;

	return id;
}

function toggleView() {
	if (calendarInstance.view.type === 'dayGridMonth') {
		calendarInstance.changeView('dayGridWeek');
		calendarInstance.setOption('contentHeight', weekViewContentHeight);
		calendarInstance.setOption('dayMaxEventRows', 5);
		toggleViewButton.innerHTML = '<span class="mdi mdi-calendar-month"></span>';
		toggleViewButton.title = 'Change to month view';

	} else {
		calendarInstance.changeView('dayGridMonth');
		calendarInstance.setOption('contentHeight', 'auto');
		calendarInstance.setOption('dayMaxEventRows', 3);
		toggleViewButton.innerHTML = '<span class="mdi mdi-calendar-range"></span>';
		toggleViewButton.title = 'Change to week view';
	}

	if (onViewChange) {
		onViewChange(calendarInstance);
	}
}

/**
 * @returns {HTMLElement}
 */
function getNewProgressBar() {
	const templatesContainer = document.querySelector('.templates');

	return templatesContainer.querySelector('.component-progress-bar').cloneNode(true);
}

export {initCalendar, getColorFromForecast, getWarningIconFromForecast, weekViewContentHeight}
